export const defaultRules = [
  {
    name: 'API Description Required',
    description: 'API must have a description',
    enabled: true,
    validate: (spec, content) => !spec.info?.description ? {
      message: 'API description missing', 
      type: 'warning',
      fix: () => addToInfoSection(content, 'description', 'API description')
    } : null
  },
  {
    name: 'Path Summaries Required',
    description: 'All operations must have summaries',
    enabled: false,
    validate: (spec, content) => {
      const paths = spec.paths || {};
      for (let path in paths) {
        for (let method in paths[path]) {
          if (typeof paths[path][method] === 'object' && !paths[path][method].summary) {
            return {
              message: `${method.toUpperCase()} ${path} missing summary`, 
              type: 'warning',
              fix: () => addToOperation(content, path, method, 'summary', `${method.toUpperCase()} ${path}`)
            };
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Operation Descriptions',
    description: 'Operations should have descriptions',
    enabled: false,
    validate: (spec, content) => {
      const paths = spec.paths || {};
      for (let path in paths) {
        for (let method in paths[path]) {
          const operation = paths[path][method];
          if (typeof operation === 'object' && !operation.description) {
            return {
              message: `${method.toUpperCase()} ${path} missing description`, 
              type: 'info',
              fix: () => addToOperation(content, path, method, 'description', 'Operation description')
            };
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Response Descriptions Required',
    description: 'All responses must have descriptions',
    enabled: false,
    validate: (spec, content) => {
      const paths = spec.paths || {};
      for (let path in paths) {
        for (let method in paths[path]) {
          const operation = paths[path][method];
          if (typeof operation === 'object' && operation.responses) {
            for (let code in operation.responses) {
              if (!operation.responses[code].description) {
                return {
                  message: `${method.toUpperCase()} ${path} response ${code} missing description`, 
                  type: 'warning',
                  fix: () => addResponseDescription(content, path, method, code, getDefaultResponseDescription(code))
                };
              }
            }
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Success Response Required',
    description: 'Operations should have 2xx responses',
    enabled: true,
    validate: (spec, content) => {
      const paths = spec.paths || {};
      for (let path in paths) {
        for (let method in paths[path]) {
          const operation = paths[path][method];
          if (typeof operation === 'object' && operation.responses) {
            const hasSuccess = Object.keys(operation.responses).some(code => code.startsWith('2'));
            if (!hasSuccess) {
              return {
                message: `${method.toUpperCase()} ${path} missing success response`, 
                type: 'warning',
                fix: () => addSuccessResponse(content, path, method)
              };
            }
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Error Responses Recommended',
    description: 'Operations should define error responses',
    enabled: false,
    validate: (spec, content) => {
      const paths = spec.paths || {};
      for (let path in paths) {
        for (let method in paths[path]) {
          const operation = paths[path][method];
          if (typeof operation === 'object' && operation.responses) {
            const hasError = Object.keys(operation.responses).some(code => code.startsWith('4') || code.startsWith('5'));
            if (!hasError) {
              return {
                message: `${method.toUpperCase()} ${path} should define error responses`, 
                type: 'info'
              };
            }
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Operation IDs Unique',
    description: 'Operation IDs must be unique',
    enabled: true,
    validate: (spec) => {
      const paths = spec.paths || {};
      const operationIds = new Set();
      for (let path in paths) {
        for (let method in paths[path]) {
          const operation = paths[path][method];
          if (typeof operation === 'object' && operation.operationId) {
            if (operationIds.has(operation.operationId)) {
              return {
                message: `Duplicate operationId "${operation.operationId}" found`, 
                type: 'error'
              };
            }
            operationIds.add(operation.operationId);
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Path Parameters Defined',
    description: 'Path parameters must be defined',
    enabled: true,
    validate: (spec) => {
      const paths = spec.paths || {};
      for (let path in paths) {
        const paramNames = (path.match(/\{([^}]+)\}/g) || []).map(p => p.slice(1, -1));
        if (paramNames.length > 0) {
          for (let method in paths[path]) {
            const operation = paths[path][method];
            if (typeof operation === 'object') {
              const definedParams = (operation.parameters || []).filter(p => p.in === 'path').map(p => p.name);
              const pathLevelParams = (paths[path].parameters || []).filter(p => p.in === 'path').map(p => p.name);
              const allDefined = [...definedParams, ...pathLevelParams];
              
              for (let paramName of paramNames) {
                if (!allDefined.includes(paramName)) {
                  return {
                    message: `Path parameter "${paramName}" in ${method.toUpperCase()} ${path} not defined`, 
                    type: 'error'
                  };
                }
              }
            }
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Request Body for POST/PUT/PATCH',
    description: 'POST/PUT/PATCH should have request bodies',
    enabled: false,
    validate: (spec) => {
      const paths = spec.paths || {};
      for (let path in paths) {
        for (let method of ['post', 'put', 'patch']) {
          const operation = paths[path][method];
          if (operation && typeof operation === 'object' && !operation.requestBody) {
            return {
              message: `${method.toUpperCase()} ${path} should have a requestBody`, 
              type: 'info'
            };
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Schema Examples Recommended',
    description: 'Schemas should include examples',
    enabled: false,
    validate: (spec) => {
      if (spec.components?.schemas) {
        for (let schemaName in spec.components.schemas) {
          const schema = spec.components.schemas[schemaName];
          if (!schema.example && !schema.examples) {
            return {
              message: `Schema "${schemaName}" missing example`, 
              type: 'info'
            };
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Tags Defined',
    description: 'Operations with tags should have tag definitions',
    enabled: false,
    validate: (spec) => {
      const definedTags = (spec.tags || []).map(t => t.name);
      const paths = spec.paths || {};
      
      for (let path in paths) {
        for (let method in paths[path]) {
          const operation = paths[path][method];
          if (typeof operation === 'object' && operation.tags) {
            for (let tag of operation.tags) {
              if (!definedTags.includes(tag)) {
                return {
                  message: `Tag "${tag}" used but not defined in top-level tags`, 
                  type: 'info'
                };
              }
            }
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Security Schemes Used',
    description: 'Defined security schemes should be used',
    enabled: false,
    validate: (spec) => {
      if (spec.components?.securitySchemes) {
        const definedSchemes = Object.keys(spec.components.securitySchemes);
        const usedSchemes = new Set();
        
        // Check global security
        if (spec.security) {
          spec.security.forEach(s => Object.keys(s).forEach(k => usedSchemes.add(k)));
        }
        
        // Check operation security
        const paths = spec.paths || {};
        for (let path in paths) {
          for (let method in paths[path]) {
            const operation = paths[path][method];
            if (typeof operation === 'object' && operation.security) {
              operation.security.forEach(s => Object.keys(s).forEach(k => usedSchemes.add(k)));
            }
          }
        }
        
        for (let scheme of definedSchemes) {
          if (!usedSchemes.has(scheme)) {
            return {
              message: `Security scheme "${scheme}" defined but not used`, 
              type: 'info'
            };
          }
        }
      }
      return null;
    }
  },
  {
    name: 'Contact Info Recommended',
    description: 'API should have contact information',
    enabled: false,
    validate: (spec, content) => !spec.info?.contact ? {
      message: 'Consider adding contact information', 
      type: 'info',
      fix: () => addContactInfo(content)
    } : null
  },
  {
    name: 'License Info Recommended',
    description: 'API should have license information',
    enabled: false,
    validate: (spec) => !spec.info?.license ? {
      message: 'Consider adding license information', 
      type: 'info'
    } : null
  },
  {
    name: 'Request/Response Bodies Use Schemas',
    description: 'Request and response bodies must reference schemas',
    enabled: true,
    validate: (spec) => {
      const paths = spec.paths || {};
      for (let path in paths) {
        for (let method in paths[path]) {
          const operation = paths[path][method];
          if (typeof operation === 'object') {
            // Check request body
            if (operation.requestBody?.content) {
              for (let contentType in operation.requestBody.content) {
                const schema = operation.requestBody.content[contentType].schema;
                if (schema && !schema.$ref && schema.type) {
                  return {
                    message: `${method.toUpperCase()} ${path} request body should use $ref to schema component`,
                    type: 'warning'
                  };
                }
              }
            }
            // Check responses
            if (operation.responses) {
              for (let code in operation.responses) {
                const response = operation.responses[code];
                if (response.content) {
                  for (let contentType in response.content) {
                    const schema = response.content[contentType].schema;
                    if (schema && !schema.$ref && schema.type) {
                      const lines = content.split('\n');
                      const pathIndex = lines.findIndex(line => line.includes(`${path}:`));
                      let line = null;
                      if (pathIndex !== -1) {
                        const methodIndex = lines.findIndex((l, i) => i > pathIndex && l.includes(`${method}:`));
                        if (methodIndex !== -1) {
                          line = methodIndex + 1;
                        }
                      }
                      return {
                        message: `${method.toUpperCase()} ${path} response ${code} should use $ref to schema component`,
                        type: 'warning',
                        line: line
                      };
                    }
                  }
                }
              }
            }
          }
        }
      }
      return null;
    }
  }
];

function addToInfoSection(content, key, value) {
  const lines = content.split('\n');
  const infoIndex = lines.findIndex(line => line.includes('info:'));
  
  if (infoIndex !== -1) {
    let insertIndex = infoIndex + 1;
    while (insertIndex < lines.length && lines[insertIndex].startsWith('  ')) {
      insertIndex++;
    }
    lines.splice(insertIndex, 0, `  ${key}: ${value}`);
  }
  
  return lines.join('\n');
}

function addToOperation(content, path, method, key, value) {
  const lines = content.split('\n');
  const pathIndex = lines.findIndex(line => line.includes(`${path}:`));
  
  if (pathIndex !== -1) {
    const methodIndex = lines.findIndex((line, i) => i > pathIndex && line.includes(`${method}:`));
    if (methodIndex !== -1) {
      let insertIndex = methodIndex + 1;
      while (insertIndex < lines.length && lines[insertIndex].startsWith('      ')) {
        insertIndex++;
      }
      lines.splice(insertIndex, 0, `      ${key}: ${value}`);
    }
  }
  
  return lines.join('\n');
}

function addResponseDescription(content, path, method, code, description) {
  const lines = content.split('\n');
  const pathIndex = lines.findIndex(line => line.includes(`${path}:`));
  
  if (pathIndex !== -1) {
    const methodIndex = lines.findIndex((line, i) => i > pathIndex && line.includes(`${method}:`));
    if (methodIndex !== -1) {
      const responseIndex = lines.findIndex((line, i) => i > methodIndex && line.includes(`${code}:`));
      if (responseIndex !== -1) {
        lines.splice(responseIndex + 1, 0, `            description: ${description}`);
      }
    }
  }
  
  return lines.join('\n');
}

function addSuccessResponse(content, path, method) {
  const lines = content.split('\n');
  const pathIndex = lines.findIndex(line => line.includes(`${path}:`));
  
  if (pathIndex !== -1) {
    const methodIndex = lines.findIndex((line, i) => i > pathIndex && line.includes(`${method}:`));
    if (methodIndex !== -1) {
      const responsesIndex = lines.findIndex((line, i) => i > methodIndex && line.includes('responses:'));
      if (responsesIndex !== -1) {
        lines.splice(responsesIndex + 1, 0, '        200:', '          description: Success');
      }
    }
  }
  
  return lines.join('\n');
}

function addContactInfo(content) {
  const lines = content.split('\n');
  const infoIndex = lines.findIndex(line => line.includes('info:'));
  
  if (infoIndex !== -1) {
    let insertIndex = infoIndex + 1;
    while (insertIndex < lines.length && lines[insertIndex].startsWith('  ')) {
      insertIndex++;
    }
    lines.splice(insertIndex, 0, '  contact:', '    email: contact@example.com');
  }
  
  return lines.join('\n');
}

function getDefaultResponseDescription(code) {
  const descriptions = {
    '200': 'Success',
    '201': 'Created',
    '204': 'No Content',
    '400': 'Bad Request',
    '401': 'Unauthorized',
    '403': 'Forbidden',
    '404': 'Not Found',
    '500': 'Internal Server Error'
  };
  return descriptions[code] || 'Response';
}